import { pitchToMidi, getVoiceRanges } from '../utils/musicTheory'

const OCTAVE_DEF = [
  { note: 'C', pos: 0, isBlack: false },
  { note: 'C#', rightIdx: 1, isBlack: true, flatNote: 'Db' },
  { note: 'D', pos: 1, isBlack: false },
  { note: 'D#', rightIdx: 2, isBlack: true, flatNote: 'Eb' },
  { note: 'E', pos: 2, isBlack: false },
  { note: 'F', pos: 3, isBlack: false },
  { note: 'F#', rightIdx: 4, isBlack: true, flatNote: 'Gb' },
  { note: 'G', pos: 4, isBlack: false },
  { note: 'G#', rightIdx: 5, isBlack: true, flatNote: 'Ab' },
  { note: 'A', pos: 5, isBlack: false },
  { note: 'A#', rightIdx: 6, isBlack: true, flatNote: 'Bb' },
  { note: 'B', pos: 6, isBlack: false },
]

const WW = 36
const BW = 22
const START_OCT = 2
const END_OCT = 6

/** 건반에 실제로 있는 음들의 좌표 (피겨 브라켓용) */
function collectKeyGeometries() {
  const geoms = []
  for (let oct = START_OCT; oct <= END_OCT; oct++) {
    const octOffset = (oct - START_OCT) * 7
    const limit = oct === END_OCT ? 1 : 12
    for (let i = 0; i < limit; i++) {
      const def = OCTAVE_DEF[i]
      if (!def.isBlack) {
        const pitch = def.note + oct
        const midi = pitchToMidi(pitch)
        if (midi == null) continue
        const left = (octOffset + def.pos) * WW
        geoms.push({ midi, left, width: 34 })
      } else {
        const sharpPitch = def.note + oct
        const midi = pitchToMidi(sharpPitch)
        if (midi == null) continue
        const left = (octOffset + def.rightIdx) * WW - Math.round(BW * 2 / 3)
        geoms.push({ midi, left, width: 22 })
      }
    }
  }
  return geoms
}

function horizontalExtentForMidiRange(geoms, minMidi, maxMidi) {
  let left = Infinity
  let right = -Infinity
  for (const g of geoms) {
    if (g.midi >= minMidi && g.midi <= maxMidi) {
      left = Math.min(left, g.left)
      right = Math.max(right, g.left + g.width)
    }
  }
  if (!Number.isFinite(left)) return null
  return { left, right }
}

function voiceBracketRows(theoryBase) {
  const ranges = getVoiceRanges(theoryBase)
  return [
    { key: 'soprano', letter: 'S', ...ranges.soprano },
    { key: 'alto', letter: 'A', ...ranges.alto },
    { key: 'tenor', letter: 'T', ...ranges.tenor },
    { key: 'bass', letter: 'B', ...ranges.bass },
  ]
}

/** 안쪽 4행 레이아웃 높이; 상단 패딩으로 S 등 첫 줄 글자가 SVG 밖으로 잘리지 않게 함 */
const BRACKET_INNER_H = 23 // 세로 60% (기존 38)
const BRACKET_TOP_PAD = 6
const BRACKET_SVG_H = BRACKET_TOP_PAD + BRACKET_INNER_H
const INSET = 2
const WHITE_KEY_H = 72 // 세로 60% (기존 120)

/** 방향에 따라 검은 건반 이름 결정: 상행 → #, 하행 → b */
function resolveBlackKeyPitch(sharpPitch, flatPitch, lastPitch) {
  if (!lastPitch) return sharpPitch
  const lastMidi = pitchToMidi(lastPitch)
  const thisMidi = pitchToMidi(sharpPitch)
  if (lastMidi == null || thisMidi == null) return sharpPitch
  if (thisMidi > lastMidi) return sharpPitch
  if (thisMidi < lastMidi) return flatPitch
  return sharpPitch
}

export default function PianoKeyboard({
  activeVoice,
  onNoteClick,
  lastPitch,
  theoryBase = 'baek',
  pressedMidis = null,
}) {
  void activeVoice
  const pressed = pressedMidis instanceof Set
    ? pressedMidis
    : new Set(Array.isArray(pressedMidis) ? pressedMidis : [])
  const totalWhite = (END_OCT - START_OCT) * 7 + 1
  const keyboardW = totalWhite * WW
  const keyGeoms = collectKeyGeometries()
  const bracketRows = voiceBracketRows(theoryBase)

  const keys = []
  for (let oct = START_OCT; oct <= END_OCT; oct++) {
    const octOffset = (oct - START_OCT) * 7
    const limit = oct === END_OCT ? 1 : 12
    for (let i = 0; i < limit; i++) {
      const def = OCTAVE_DEF[i]
      if (!def.isBlack) {
        const pitch = def.note + oct
        const midi = pitchToMidi(pitch)
        const isPressed = midi != null && pressed.has(midi)
        keys.push(
          <div
            key={pitch}
            className={`wkey${isPressed ? ' active' : ''}`}
            style={{ left: (octOffset + def.pos) * WW }}
            onMouseDown={(e) => { e.preventDefault(); onNoteClick(pitch) }}
            title={pitch}
          >
            {def.note === 'C' && (
              <span className="key-label">{pitch}</span>
            )}
          </div>,
        )
      } else {
        const sharpPitch = def.note + oct
        const flatPitch = def.flatNote + oct
        const midi = pitchToMidi(sharpPitch)
        const isPressed = midi != null && pressed.has(midi)
        keys.push(
          <div
            key={sharpPitch}
            className={`bkey${isPressed ? ' active' : ''}`}
            style={{
              left: (octOffset + def.rightIdx) * WW - Math.round(BW * 2 / 3),
            }}
            onMouseDown={(e) => {
              e.preventDefault()
              onNoteClick(resolveBlackKeyPitch(sharpPitch, flatPitch, lastPitch))
            }}
            title={`${sharpPitch} / ${flatPitch}`}
          />,
        )
      }
    }
  }

  const rowH = BRACKET_INNER_H / bracketRows.length

  return (
    <div className="piano-wrap">
      <div className="piano-stack" style={{ width: keyboardW }}>
        <svg
          className="piano-brackets-svg"
          width={keyboardW}
          height={BRACKET_SVG_H}
          aria-hidden
        >
          {bracketRows.map((row, idx) => {
            const ext = horizontalExtentForMidiRange(keyGeoms, row.minMidi, row.maxMidi)
            if (!ext) return null
            const x1 = Math.max(0, ext.left + INSET)
            const x2 = Math.min(keyboardW, ext.right - INSET)
            if (x2 <= x1) return null
            const yTop = BRACKET_TOP_PAD + idx * rowH + 2
            const yBot = BRACKET_TOP_PAD + (idx + 1) * rowH - 1
            const yMid = yTop + (yBot - yTop) * 0.32
            const cx = (x1 + x2) / 2
            const d = `M ${x1} ${yBot} L ${x1} ${yMid} L ${x2} ${yMid} L ${x2} ${yBot}`
            return (
              <g key={row.key}>
                <path
                  d={d}
                  className="piano-bracket-path"
                  fill="none"
                />
                <text
                  x={cx}
                  y={(yTop + yMid) / 2}
                  textAnchor="middle"
                  className="piano-bracket-label"
                >
                  {row.letter}
                </text>
              </g>
            )
          })}
        </svg>
        <div
          className="piano-keyboard"
          style={{ width: keyboardW, height: WHITE_KEY_H }}
        >
          {keys}
        </div>
      </div>
    </div>
  )
}
